<template>
  <div class="h-100 d-flex flex-column align-center justify-space-around fit-content mx-auto ga-5">
    <ConnectingLine
      :startElementId="`system-${system.index}`"
      :endElementId="`domain-event-${system.index}-${event.index}`"
      :refreshKey="refreshKey"
      direction="auto"
      offsetParentId="domain-model"
    />
    <div :id="`domain-event-${system.index}-${event.index}`" class="domain-event-box">
      <div
        v-if="domainEventCard"
        class="card-item hover-container fit-content"
        :style="{ 'border-color': cardTypeColor(domainEventCardType), cursor: 'pointer' }"
        @click="onCardClick"
      >
        <select-entity-combobox
          :id="domainEventCard.id"
          :events="[event]"
          :cardIds="[domainEventCard.id]"
          :schemaType="SchemaType.DOMAIN_EVENT"
          :style="{ '--borderColor': cardTypeColor(domainEventCardType), '--borderTopWidth': '1px' }"
        />
        <div v-if="showEntities && linkedDomainEvent?.fields?.length > 0" class="py-2">
          <template v-for="(field, index4) in linkedDomainEvent.fields" :key="index4">
            <div class="field-name d-flex align-center">
              <quick-rename-input
                :id="`domain-event-${event.id}-field-${index4}`"
                :value="field.name"
                @onChange="(e) => blurField(e, event, domainEventCard.schemaId, index4)"
                :density="`compact`"
                :max-length="30"
                class="flex-1-0"
                :class="{ 'required-field': field.tags?.includes('REQUIRED') }"
                :disabled="store.state.isWorkflowDisabled"
              />
              <span v-if="dataTypeLabel(field)" class="data-type-label">{{ dataTypeLabel(field) }}</span>
              <v-chip
                v-if="field.relatedEntityId && getRelatedEntity(field)"
                density="compact"
                size="small"
                class="px-2 ml-1 cursor-default"
              >
                {{ field.cardinality === 'one-to-many'
                  ? `[ ${getRelatedEntity(field)?.name} ]`
                  : `${getRelatedEntity(field)?.name}` }}
              </v-chip>
            </div>
            <div
              v-for="(subField, subIndex) in (field.relatedEntityId && field.fields) || []"
              :key="`${index4}-sub-${subIndex}`"
              class="pl-4 field-name d-flex align-center"
            >
              <quick-rename-input
                :id="`domain-event-${event.id}-field-${index4}-sub-${subIndex}`"
                :value="subField.name"
                @onChange="(e) => blurSubField(e, event, domainEventCard.schemaId, index4, subIndex)"
                :density="`compact`"
                :max-length="30"
                class="flex-1-0"
                :disabled="store.state.isWorkflowDisabled"
              />
            </div>
          </template>
        </div>
        <div v-if="linkedDomainEvent" class="show-on-hover">
          <edit-domain-event :schema="linkedDomainEvent" />
        </div>
      </div>
      <v-btn
        v-else-if="domainEventCardType"
        class="add-card-btn"
        variant="elevated"
        :style="{ '--border-color': cardTypeColor(domainEventCardType) }"
        block
        @click="createCard(event.id, domainEventCardType.id, `<p>${event.description}</p>`)"
        :disabled="store.state.isWorkflowDisabled"
      >
        + {{ domainEventCardType.title }}
      </v-btn>
    </div>
  </div>
</template>

<script setup>
import { useStore } from 'vuex'
import { computed } from 'vue'
import { useRoute, useRouter } from 'vue-router'
import SelectEntityCombobox from '@/components/SelectEntityCombobox.vue'
import ConnectingLine from '@/components/ConnectingLine.vue'
import QuickRenameInput from '@/components/QuickRenameInput'
import EditDomainEvent from '@/components/EditDomainEvent.vue'
import { diagramHelpers } from '@/mixins/diagramHelpers'
import useDomainModel from '@/composables/useDomainModel'
import { DataTypeShortLabels, SchemaType } from '@/constants/schema'

const {
  system,
  event,
  showEntities,
  refreshKey
} = defineProps({
  system: Object,
  event: Object,
  boundedContext: Object,
  showEntities: Boolean,
  refreshKey: Number
})

const store = useStore()
const route = useRoute()
const router = useRouter()
const workflow = computed(() => store.state.workflow.workflow)
const cardTypes = computed(() => store.state.cardTypes.cardTypes)
const domainEventCardType = computed(() => diagramHelpers.methods.getDomainEventCardType(cardTypes.value))

const {
  cardTypeColor,
  getRelatedEntity,
  blurField,
  blurSubField,
  createCard
} = useDomainModel()

const domainEventCard = computed(() => system.domainEventCards?.[event.index] || null)

const linkedDomainEvent = computed(() => {
  if (!domainEventCard.value?.schemaId) {
    return null
  }

  return (workflow.value?.schemas ?? []).find((s) => s.id === domainEventCard.value.schemaId && s.type === SchemaType.DOMAIN_EVENT)
})

const onCardClick = (e) => {
  const interactive = e.target.closest('input, textarea, [contenteditable="true"], .v-field, .v-input, .v-combobox, button, .v-btn, .v-chip')
  if (interactive) {
    return
  }
  router.push({
    path: `/workflow/${route.params.projectId}/${route.params.id}/${event.id}`
  }).catch(() => {})
}

const dataTypeLabel = (field) => {
  return DataTypeShortLabels[field.dataType] || ''
}
</script>

<style scoped>
.domain-event-box {
  z-index: 1;
}
</style>
